import { useCallback, useEffect, useState } from "react";

import { format } from "date-fns";
import isEmpty from "lodash.isempty";

import { DatePickerValue } from "./DatePicker.types";
import { DateTimeChange, PickerStrategy } from "./DatePickerStrategy";
import { getCurrentTimeRounded,setSelectedDateTime } from "./utils";

export interface UseSingleDatePickerParams {
  value?: DatePickerValue;
  onChange?: (value?: Date | string) => void;
  onApply?: (value: Date | string) => void;
  onClear?: () => void;
  displayInUTC?: boolean;
  shouldExcludePast?: boolean;
}

const DEFAULT_TIME = '12:00';

export const useSingleDatePicker = ({
  value,
  onChange,
  onApply,
  onClear,
  displayInUTC,
  shouldExcludePast
}: UseSingleDatePickerParams): PickerStrategy => {
  const getDefaultTime = useCallback(() => {
    return shouldExcludePast ? getCurrentTimeRounded() : DEFAULT_TIME;
  }, [shouldExcludePast]);

  const [date, setDate] = useState<Date | undefined>(
    !isEmpty(value) ? new Date(value as string | Date) : undefined
  );
  const [fromValue, setFromValue] = useState(() => shouldExcludePast ? getCurrentTimeRounded() : DEFAULT_TIME);

  const onDateTimeChange = useCallback(
    ({ date: newDate, time: newTime }: DateTimeChange) => {
      const {
        isoString,
        date: newDateObj,
        fromValue: newFromValue
      } = setSelectedDateTime(date, newDate, newTime, fromValue);
      setDate(newDateObj);
      setFromValue(newFromValue);
      onChange?.(isoString);
    },
    [date, fromValue, onChange]
  );

  const getDisplayDate = useCallback(() => {
    if (!date) {
      return undefined;
    }
    if (displayInUTC) {
      const year = date.getUTCFullYear();
      const month = date.toLocaleString('default', {
        month: 'long',
        timeZone: 'UTC'
      });
      const day = date.getUTCDate();
      const hours = date.getUTCHours().toString().padStart(2, '0');
      const minutes = date.getUTCMinutes().toString().padStart(2, '0');
      return `${month} ${day}, ${year} ${hours}:${minutes} UTC`;
    }
    return format(date, 'PPP HH:mm');
  }, [date, displayInUTC]);

  const onValueChange = useCallback(() => {
    if (value === undefined || isEmpty(value)) {
      setDate(undefined);
      setFromValue(getDefaultTime());
      return;
    }

    const valueAsDate = new Date(value as Date | string);
    setDate(valueAsDate);
    const h = valueAsDate.getHours().toString().padStart(2, '0');
    const m = valueAsDate.getMinutes().toString().padStart(2, '0');
    setFromValue(`${h}:${m}`);
  }, [value, getDefaultTime]);

  useEffect(() => {
    onValueChange();
  }, [value, onValueChange]);

  const handleClear = () => {
    setDate(undefined);
    setFromValue(getDefaultTime());
    onChange?.(undefined);
    onClear?.();
  };

  const handleApply = () => {
    if (onApply) {
      if (date) {
        onApply(date);
      }
    }
  };

  return {
    value: date,
    getDisplayDate,
    onValueChange,
    select: (date) => onDateTimeChange({ date: date as Date | undefined }),
    apply: handleApply,
    clear: handleClear,
    isApplyDisabled: !date,
    fromValue,
    toValue: undefined,
    changeTime: onDateTimeChange,
    onFromTimeChange: undefined,
    onToTimeChange: undefined,
  };
};